import { memo, Suspense } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle, XCircle } from 'lucide-react';
import DropdownButton from '../Common/DropDown';
import SprintGoalSuccessTooltipIcon from './SprintGoalSuccessTooltipIcon';
import { standupPointsModeTabLabel } from '../../utils/boardUtils';

function StandUpSprintGoalSuccessSection({
  selectedDeveloper,
  selectedValueDisplay,
  theme,
  isSprintGoalStoryPoints,
  setIsSprintGoalStoryPoints,
  sprintGoalWorkMode,
  setSprintGoalWorkMode,
  lockToHours,
  lockToPoints,
  pointsSourceType,
  sprintdata,
  isUsingStoryPoints,
  heavyChartsReady,
  chartSuspenseFallback,
  sprintGoalRechartsPayload,
  SprintGoalBarChart,
  isGithubIssues = false,
}) {
  return (
    <div
      className={`flex flex-col gap-[10px] basis-[200px] flex-grow min-w-0 overflow-visible transition-all duration-300 ease-in-out mr-2 ${
        selectedDeveloper
          ? 'opacity-0 scale-50 pointer-events-none'
          : 'opacity-100 scale-100'
      }`}
    >
      <div className="p-[10px] bg-[#FFFFFF] basis-[100px] flex-grow dark:bg-[#182433] dark:text-[#C8C8C8] text-black rounded-[10px] shadow-[0_1px_20px_0_rgba(0,0,0,0.1)] dark:shadow-md border border-[#E5E5E5] dark:border-[#25384F] w-full h-[297px] flex flex-col min-h-0 overflow-hidden">
        <div className="flex items-center justify-between mb-6 gap-1 flex-nowrap min-w-0 shrink-0">
          <div className="flex items-center gap-1 group min-w-0 overflow-visible">
            <h2 className="text-sm md:text-md text-[#0A2342] dark:text-white mb-0 truncate whitespace-nowrap">
              {`${selectedValueDisplay} Goal Success`}
            </h2>
            <div className="relative group">
              <SprintGoalSuccessTooltipIcon theme={theme} selectedValueDisplay={selectedValueDisplay} placement="top-end" />
            </div>
          </div>
          <div className="flex items-center gap-1 ml-auto flex-shrink-0">
            <DropdownButton
              placeholder="Scope"
              options={[
                { label: 'With added', value: 'with_added_work' },
                { label: 'Without added', value: 'without_added_work' },
              ]}
              selectedOption={
                sprintGoalWorkMode === 'without_added_work'
                  ? 'Without added'
                  : 'With added'
              }
              onSelect={(option) => setSprintGoalWorkMode(option.value)}
              width="xs"
              height="xs"
              textSize="xs"
              type="default"
              showSearch={false}
            />
            <div className="inline-flex items-center bg-transparent dark:bg-[#242B34] rounded-full p-0.5 border border-[#E5E5E5] dark:border-[#101010]">
              <button
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                  isSprintGoalStoryPoints
                    ? 'dark:bg-[#066FD1] bg-[#24527A] text-white'
                    : 'text-[#24527A] dark:text-gray-500'
                }`}
                onClick={() => setIsSprintGoalStoryPoints(true)}
                disabled={lockToHours}
              >
                {/* {!isSprintGoalStoryPoints
                  ? 'SP'
                  : pointsSourceType === 'effort'
                  ? ' Effort'
                  : 'SP'} */}
                     {standupPointsModeTabLabel({
                  pointsSourceType,
                  isGithubIssues,
                  modeSelected: isSprintGoalStoryPoints,
                })}
              </button>
              <button
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                  !isSprintGoalStoryPoints
                    ? 'dark:bg-[#066FD1] bg-[#24527A] text-white'
                    : 'text-[#24527A] dark:text-gray-500'
                }`}
                onClick={() => setIsSprintGoalStoryPoints(false)}
                disabled={lockToPoints}
              >
                Hrs
              </button>
            </div>
          </div>
        </div>
        <div
          className="flex justify-center gap-10 mb-0 mt-0 flex-wrap relative"
          style={{ height: '20px' }}
        >
          {sprintdata.map((item, index) => {
            const isStoryPointMode =
              !lockToHours && isUsingStoryPoints && isSprintGoalStoryPoints;
            const isHourMode = lockToHours
              ? !isSprintGoalStoryPoints
              : !isUsingStoryPoints && !isSprintGoalStoryPoints;
            const committedValue = isSprintGoalStoryPoints
              ? sprintGoalWorkMode === 'without_added_work'
                ? item.plannedStoryPoints
                : item.committed
              : sprintGoalWorkMode === 'without_added_work'
              ? item.plannedOriginalEstimateHrs
              : item.committedHours;
            const completedValue = isSprintGoalStoryPoints ? item.completed : item.completedHours;
            const isEmpty = isStoryPointMode
              ? committedValue === 0 && completedValue === 0
              : isHourMode
              ? committedValue === 0 && completedValue === 0
              : true;
            const isSuccess = isStoryPointMode
              ? committedValue === completedValue
              : isHourMode
              ? committedValue === completedValue
              : false;

            const getIconPosition = (length, idx) => {
              const positions = {
                1: ['47%'],
                3: ['10%', '45%', '80%'],
                4: ['5%', '32%', '58%', '84%'],
                5: ['3%', '24%', '45%', '66%', '87%'],
              };
              return (
                positions[length]?.[idx] || `calc(${(idx + 0.2) * (100 / length)}% - 1px)`
              );
            };

            const leftPosition = getIconPosition(sprintdata.length, index);
            const needsTransform = sprintdata.length === 1;

            return (
              <div
                key={index}
                className="flex flex-col items-center absolute"
                style={{
                  left: leftPosition,
                  transform: needsTransform ? 'translateX(-50%)' : 'none',
                }}
              >
                {!isEmpty ? (
                  isSuccess ? (
                    <CheckCircle className="text-green-500 w-3 h-3" />
                  ) : (
                    <XCircle className="text-red-500 w-3 h-3" />
                  )
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="w-full flex-1 ml-0 min-h-0 relative overflow-hidden">
          {heavyChartsReady ? (
            <Suspense fallback={chartSuspenseFallback}>
              <SprintGoalBarChart
                chartData={sprintGoalRechartsPayload.chartData}
                maxValue={sprintGoalRechartsPayload.maxValue}
                barCategoryGap={sprintGoalRechartsPayload.barCategoryGap}
                theme={theme}
                sprintdata={sprintdata}
                isSprintGoalStoryPoints={isSprintGoalStoryPoints}
              />
            </Suspense>
          ) : (
            chartSuspenseFallback
          )}
        </div>
        <div
          className={`flex justify-center items-center space-x-4 px-2 text-xs mt-2 ${
            theme === 'light' ? 'text-[#626262]' : 'text-[#A3B1C9]'
          }`}
        >
          <div className="flex items-center">
            <span
              className="w-3 h-3 rounded-full inline-block mr-2"
              style={{ backgroundColor: theme === 'light' ? '#0077E6' : '#066FD1' }}
            />
            Committed
          </div>
          <div className="flex items-center">
            <span
              className="w-3 h-3 rounded-full inline-block mr-2"
              style={{ backgroundColor: '#2FB344' }}
            />
            Completed
          </div>
        </div>
      </div>
    </div>
  );
}

StandUpSprintGoalSuccessSection.propTypes = {
  selectedDeveloper: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.object]),
  selectedValueDisplay: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired,
  isSprintGoalStoryPoints: PropTypes.bool.isRequired,
  setIsSprintGoalStoryPoints: PropTypes.func.isRequired,
  sprintGoalWorkMode: PropTypes.oneOf(['with_added_work', 'without_added_work']).isRequired,
  setSprintGoalWorkMode: PropTypes.func.isRequired,
  lockToHours: PropTypes.bool.isRequired,
  lockToPoints: PropTypes.bool.isRequired,
  pointsSourceType: PropTypes.string,
  sprintdata: PropTypes.array.isRequired,
  isUsingStoryPoints: PropTypes.bool.isRequired,
  heavyChartsReady: PropTypes.bool.isRequired,
  chartSuspenseFallback: PropTypes.node.isRequired,
  sprintGoalRechartsPayload: PropTypes.shape({
    chartData: PropTypes.array,
    maxValue: PropTypes.number,
    barCategoryGap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  SprintGoalBarChart: PropTypes.elementType.isRequired,
   isGithubIssues: PropTypes.bool,
};

export default memo(StandUpSprintGoalSuccessSection);
